import React, { useState } from "react";
import { NavigateFunction } from "react-router-dom";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { Badge, Typography, Card } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { EvalResult } from "../../services/evals";
import { ResultsTable } from "./ResultsTable";

interface RunContainerProps {
  runName: string;
  results: EvalResult[];
  expandedResultId: string | null;
  setExpandedResultId: (id: string | null) => void;
  deleteResultMutation: {
    mutate: (id: string) => void;
  };
  navigate: NavigateFunction;
}

export const RunContainer: React.FC<RunContainerProps> = ({
  runName,
  results,
  expandedResultId,
  setExpandedResultId,
  deleteResultMutation,
  navigate,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Calculate stats
  const passedCount = results.filter(r => r.overall_score >= 4).length;
  const okCount = results.filter(r => r.overall_score >= 3 && r.overall_score < 4).length;
  const failedCount = results.filter(r => r.overall_score < 3).length;
  const avgScore = (results.reduce((sum, r) => sum + r.overall_score, 0) / results.length).toFixed(
    2
  );

  // Get latest execution time
  const latestTime = results.length > 0 ? results[0].created_at : "";

  return (
    <Card className="al-p-0 al-overflow-hidden al-max-w-full">
      {/* Run Header */}
      <div
        className="al-p-4 al-bg-gray-50 al-cursor-pointer hover:al-bg-gray-100 al-transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
      >
        <Stack direction="row" justify="between" align="center" className="al-flex-wrap al-gap-4">
          <Stack direction="row" align="center" gap="md" className="al-flex-1 al-min-w-0">
            {/* Expand/Collapse Icon */}
            <div className="al-flex-shrink-0">
              {isExpanded ? (
                <IconChevronDown className="al-w-5 al-h-5 al-text-gray-500" />
              ) : (
                <IconChevronRight className="al-w-5 al-h-5 al-text-gray-500" />
              )}
            </div>

            {/* Run Name */}
            <div className="al-flex-1 al-min-w-0">
              <Typography variant="h4" className="al-break-words al-overflow-hidden">
                {runName}
              </Typography>
              {latestTime && (
                <Typography size="xs" className="al-text-gray-500 al-mt-1 al-break-words">
                  Latest: {new Date(latestTime).toLocaleString()}
                </Typography>
              )}
            </div>
          </Stack>

          {/* Stats */}
          <Stack direction="row" align="center" gap="md" className="al-flex-shrink-0 al-flex-wrap">
            <Stack direction="row" align="center" gap="xs" className="al-flex-wrap">
              <Badge className="!al-bg-green-100 !al-text-green-700 al-border al-border-green-200 al-whitespace-nowrap">
                {passedCount} Pass
              </Badge>
              <Badge className="!al-bg-yellow-100 !al-text-yellow-700 al-border al-border-yellow-200 al-whitespace-nowrap">
                {okCount} Ok
              </Badge>
              <Badge className="!al-bg-red-100 !al-text-red-700 al-border al-border-red-200 al-whitespace-nowrap">
                {failedCount} Fail
              </Badge>
            </Stack>

            <div className="al-text-right al-whitespace-nowrap">
              <Typography size="xs" className="al-text-gray-500">
                Avg Score
              </Typography>
              <Typography size="sm" weight="medium">
                {avgScore}/5
              </Typography>
            </div>

            <Badge variant="secondary" className="al-text-sm al-whitespace-nowrap">
              {results.length} {results.length === 1 ? "result" : "results"}
            </Badge>
          </Stack>
        </Stack>
      </div>

      {/* Results Table */}
      {isExpanded && (
        <div className="al-p-4 al-overflow-x-hidden al-max-w-full">
          <ResultsTable
            results={results}
            expandedResultId={expandedResultId}
            setExpandedResultId={setExpandedResultId}
            deleteResultMutation={deleteResultMutation}
            navigate={navigate}
          />
        </div>
      )}
    </Card>
  );
};
